import { entity, legalPaths } from '../entity';
import {
  P, H3, Ul, Callout, Ext, Tok,
} from '../blocks';
import type { LegalDoc } from '../types';

// Terms of Service (English).
//
// Two structural things to preserve if you edit this:
//
//  1. Section "rules" incorporates the Community Guidelines by reference. That
//     sentence is the only thing giving the Guidelines legal force — without it
//     they are a blog post. Keep the Guidelines revisable on their own so a new
//     abuse pattern does not require a contract amendment.
//
//  2. Section "disputes" is drafted to survive Heckman v. Live Nation (9th Cir.
//     2024, cert denied Oct 2025), which voided an entire arbitration agreement
//     over bespoke machinery. So: standard bilateral arbitration, a named
//     mainstream provider, no bellwether provisions, no batching, no
//     confidentiality requirement and no shortened limitations period. The class
//     waiver is deliberately non-severable — if it falls, arbitration falls with
//     it and the claim goes to court. Class *arbitration* is the worst outcome
//     available and must never be the fallback.
//
// The content licence in "your-content" is perpetual for Spots, tribys and
// delivered messages because spots.created_by_id is ON DELETE RESTRICT — that
// content outlives its author's account, which is also why users rows are
// anonymised rather than deleted. Narrowing the licence would contradict the
// deletion section of the Privacy Policy.

export const termsEn: LegalDoc = {
  lang: 'en',
  title: 'Terms of Service',
  updatedLabel: `Last updated ${entity.effectiveDate}`,
  contentsLabel: 'Contents',
  altHref: legalPaths.termsEs,
  altLabel: 'Ver en español',
  sections: [
    {
      id: 'about',
      heading: '1. These Terms',
      body: (
        <>
          <P>
            Tribys is an app for finding groups of people who share your
            interests, and then actually meeting them in person. These Terms are
            the agreement between you and{' '}
            <Tok value={entity.legalName} />, a{' '}
            <Tok value={entity.formedIn} /> limited liability company
            (&quot;Tribys&quot;, &quot;we&quot;, &quot;us&quot;). They apply when
            you create an account, use the app, or use any page on tribys.com.
          </P>
          <P>
            By creating an account you accept these Terms. If you do not accept
            them, do not use Tribys.
          </P>
          <P>
            Two other documents form part of this agreement, and you should read
            both:
          </P>
          <Ul
            items={[
              <>Our <a href={legalPaths.guidelines}>Community Guidelines</a>, which set out what you may and may not do on Tribys.</>,
              <>Our <a href={legalPaths.privacy}>Privacy Policy</a>, which explains what we collect and why. If you use identity verification, the <a href={legalPaths.biometric}>Biometric &amp; Consumer Health Data Notice</a> applies as well.</>,
            ]}
          />
          <Callout>
            <strong>
              Section 17 requires most disputes to go to individual arbitration
              and waives your right to participate in a class action.
            </strong>{' '}
            It contains a 60-day informal resolution step you must complete
            first, and a 30-day right to opt out of arbitration entirely. If you
            live in Quebec or Mexico, section 18 does not apply to you. Please
            read it.
          </Callout>
        </>
      ),
    },
    {
      id: 'eligibility',
      heading: '2. You must be 18 or older',
      body: (
        <>
          <P>
            <strong>Tribys is for adults only.</strong> You must be at least 18
            years old to create an account. There is no version of Tribys for
            under-18s, and we do not offer one, because the app is built around
            meeting strangers face to face.
          </P>
          <P>
            We check your date of birth at sign-up and reject anyone under 18. If
            we discover that an account belongs to someone under 18 we delete it.
            If you believe a minor is using Tribys, report the account in the app
            or email <Tok value={entity.supportEmail} />.
          </P>
          <P>
            You must also not be barred from using Tribys under the laws that
            apply to you, and you must not have been banned from Tribys before.
          </P>
        </>
      ),
    },
    {
      id: 'account',
      heading: '3. Your account',
      body: (
        <>
          <P>
            You need an account to use Tribys. When you create one you agree to
            give accurate information and to keep it accurate — in particular
            your name, date of birth and profile photo. Tribys only works if
            people are who they say they are.
          </P>
          <Ul
            items={[
              'One account per person. Do not create a second account, and do not create an account for anyone else.',
              'Your account is yours alone. Do not share it, sell it, rent it, or transfer it.',
              'Keep your sign-in details secure, and tell us promptly if you think someone else has access.',
              'Do not create a new account to get around a ban.',
            ]}
          />
          <P>
            You are responsible for what happens on your account. If you signed
            in with Google or Apple, keeping that account secure is
            part of keeping your Tribys account secure.
          </P>
        </>
      ),
    },
    {
      id: 'meeting',
      heading: '4. Meeting people in real life — please read this',
      body: (
        <>
          <Callout>
            <strong>
              Tribys introduces you to strangers and helps you arrange to meet
              them in person. We do not run background checks, we do not vet
              anyone&apos;s character or intentions, we are not present at
              meetings, and we are not a party to any plan you make. You decide
              who to meet, where to meet, and whether to go.
            </strong>
          </Callout>
          <P>
            We want to be direct about this rather than bury it. Meeting people
            you do not know carries real risk, and no feature of this app removes
            that risk. Identity verification confirms that a document and a face
            match — it says nothing about whether a person is safe, honest or
            pleasant to be around. Reports and bans are reactive: they happen
            after something has gone wrong.
          </P>
          <P>
            <strong>You take responsibility for your own safety.</strong> To the
            fullest extent the law allows, you assume the risks of meeting other
            members and of attending any event or going to any place you find
            through Tribys, and you agree that we are not liable for the conduct
            of other members, whether online or offline. Sections 14 and 15 set
            out the limits of our responsibility.
          </P>
          <P>Some practical things we would ask you to do:</P>
          <Ul
            items={[
              'Meet in public places, especially the first time. Our Spots are mostly public venues for a reason.',
              'Tell someone you trust where you are going and who you are meeting.',
              'Keep conversations in the app until you are comfortable moving elsewhere.',
              'Do not send money to anyone you have met through Tribys, and treat any request for money as a scam.',
              'Trust your instincts and leave if something feels wrong. You do not owe anyone an explanation.',
              'Report the member afterwards. It is how we find people who do this repeatedly.',
            ]}
          />
          <P>
            If you are in immediate danger, contact your local emergency
            services first. Then tell us, so we can act on the account.
          </P>
        </>
      ),
    },
    {
      id: 'verification',
      heading: '5. What the verified badge means',
      body: (
        <>
          <P>
            Identity verification is optional. If you complete it, our
            verification provider reads your government-issued ID and checks that
            a live person matching it is present, and you get a verified badge.
            Later profile photos are compared against the reference image so that
            someone cannot verify with their own ID and then put a different
            person&apos;s face on the profile.
          </P>
          <P>
            <strong>
              The badge means one thing: the document and the face matched at the
              time of the check.
            </strong>{' '}
            It is not a character reference, a criminal-record check, a
            safety rating, or a promise about anyone&apos;s behaviour. Treat a
            verified member with exactly the caution you would treat any
            stranger.
          </P>
          <P>
            Verification can also fail or succeed wrongly — it is an automated
            check on an image. See the{' '}
            <a href={legalPaths.biometric}>Biometric &amp; Consumer Health Data Notice</a>{' '}
            for what we collect, how long we keep it, and how to withdraw your
            consent, and section 6 of the{' '}
            <a href={legalPaths.privacy}>Privacy Policy</a> for how to ask a
            human to review an automated decision.
          </P>
        </>
      ),
    },
    {
      id: 'rules',
      heading: '6. Rules of use',
      body: (
        <>
          <P>
            <strong>
              Our <a href={legalPaths.guidelines}>Community Guidelines</a> are
              part of these Terms, and breaking them is a breach of this
              agreement.
            </strong>{' '}
            They cover conduct, content and safety, and we may update them as new
            problems appear. Read them — they are shorter than this document and
            more likely to affect your day.
          </P>
          <P>In addition, you agree not to:</P>
          <Ul
            items={[
              'use Tribys for anything unlawful, or to arrange anything unlawful;',
              'scrape, crawl, harvest or bulk-collect other members’ information, profiles, photos or locations, by any means, including automated ones;',
              'reverse engineer, decompile or attempt to extract our source code, except where the law expressly permits it;',
              'interfere with, overload or probe our systems, or attempt to bypass rate limits, authentication or any security measure;',
              'use bots, scripts or automated tools to create accounts, send messages, join groups or interact with other members;',
              'access Tribys other than through our app and our website, or build a competing or derivative service using our data;',
              'impersonate anyone, including us, or misrepresent your affiliation with any person or organisation;',
              'resell, sublicense or commercially exploit any part of Tribys, or use it to advertise or promote a business except as the Community Guidelines allow;',
              'upload anything containing malware, or use Tribys to distribute it;',
              'help anyone else do any of the above.',
            ]}
          />
        </>
      ),
    },
    {
      id: 'your-content',
      heading: '7. Your content',
      body: (
        <>
          <P>
            &quot;Your content&quot; means anything you put into Tribys: your
            profile and photos, tribys you create, Spots you add, messages you
            send, interests you create, reports you file and anything else you
            submit.
          </P>
          <H3>You keep ownership</H3>
          <P>
            Your content stays yours. We do not claim ownership of it, and we do
            not sell it.
          </P>
          <H3>The permission you give us</H3>
          <P>
            To run Tribys we need permission to store and show your content. You
            grant us a worldwide, non-exclusive, royalty-free licence to host,
            store, cache, reproduce, resize, crop, publish, publicly display and
            distribute your content, and to sublicense those rights to the
            hosting and content-delivery providers we use, for the purpose of
            operating, securing and improving Tribys. We do not use your content
            for advertising, and we run no advertising or analytics services.
          </P>
          <P>
            This includes showing your display name and profile photo in link
            previews when you or another member shares a triby, Spot or profile
            link — those previews are readable by anyone who has the link. See
            the <a href={legalPaths.privacy}>Privacy Policy</a> for which pages
            are indexable by search engines and which are not.
          </P>
          <H3>How long that permission lasts</H3>
          <P>
            Generally it ends when you delete the content or your account. There
            is one exception, and it is worth understanding:
          </P>
          <Callout>
            For <strong>Spots you create, tribys you create, and messages you
            have already sent to other members</strong>, the licence is perpetual
            and irrevocable. Other people rely on that content — a Spot may be
            the venue for a group that still meets there, and a conversation
            belongs to everyone in it. Deleting your account removes your name
            and your identifiers from it, and empties the text of your messages,
            but the Spot or triby itself remains for the people using it. The
            deletion section of the{' '}
            <a href={legalPaths.privacy}>Privacy Policy</a> describes exactly what
            is removed and what stays.
          </Callout>
          <H3>What you promise us about it</H3>
          <P>By submitting content you confirm that:</P>
          <Ul
            items={[
              'you own it, or you have all the rights and permissions needed to submit it and to give us the licence above;',
              'it does not infringe anyone’s copyright, trademark, trade secret, database right, right of publicity, or right of privacy;',
              <><strong>you took the photographs you upload, or you have the photographer&apos;s permission.</strong> A photo you found online, on social media, on a review site or on a business&apos;s own website belongs to whoever took it — not to the venue, and not to you.</>,
              'anyone identifiable in a photo you upload has agreed to appear in it;',
              'it is accurate, and it is not misleading about who you are or what a place or event is;',
              'it does not break these Terms or the Community Guidelines.',
            ]}
          />
          <P>
            You are responsible for your content and for the consequences of
            submitting it. Section 16 explains what that means if a third party
            makes a claim about it.
          </P>
          <H3>Feedback</H3>
          <P>
            If you send us suggestions about Tribys, we may use them freely and
            without owing you anything. Please do not send us anything you
            consider confidential.
          </P>
        </>
      ),
    },
    {
      id: 'spots',
      heading: '8. Spots, businesses and events',
      body: (
        <>
          <P>
            A Spot is a place or an event on the Tribys map. Spots are{' '}
            <strong>created by members, not by us</strong>. We do not verify them,
            we are not affiliated with the places or events listed, and we do not
            endorse them. A Spot may be inaccurate, out of date, closed, or
            simply wrong. Check before you go, and treat opening hours, addresses
            and event times as a member&apos;s best guess rather than fact.
          </P>
          <P>
            Because Spots often describe real businesses and real events, this is
            where third-party rights are most likely to come up. If you create a
            Spot, you are responsible for it.
          </P>
          <H3>What is fine</H3>
          <P>
            Identifying a place by its real name and giving its address, its
            location, its opening hours and a plain factual description of it.
            That is how a map is useful, and referring to a business by name in
            order to say where it is does not require anyone&apos;s permission.
          </P>
          <H3>What is not</H3>
          <Ul
            items={[
              <><strong>Photos you did not take.</strong> This is the single most common problem. Do not upload photos pulled from a business&apos;s website or social media, from Google, from a review site, or from a press kit. Take your own, or leave the Spot without a photo.</>,
              <><strong>Logos, promotional artwork, event posters, flyers and menus.</strong> These are somebody&apos;s designs. Do not upload them as Spot images.</>,
              <><strong>Implying a connection that does not exist.</strong> Do not present a Spot as official, sponsored, endorsed by or affiliated with a business, venue, brand or organiser unless it genuinely is.</>,
              <><strong>Using a Spot as an advertisement.</strong> Copying marketing copy, adding promotional or discount claims, or listing contact details to route business to yourself is not what Spots are for. See the Community Guidelines.</>,
              <><strong>Copying a listings database.</strong> Do not bulk-import Spots from another map, directory or listings service.</>,
            ]}
          />
          <H3>If it is your business or your event</H3>
          <P>
            You are welcome to add it, and to upload your own photos of it.
            Everything else in these Terms and the Community Guidelines still
            applies — a Spot is a place on a map, not an advertising slot.
          </P>
          <H3>If someone has listed your business, venue or event</H3>
          <P>
            Report the Spot in the app using the{' '}
            <strong>Intellectual property</strong> reason, or write to{' '}
            <Tok value={entity.copyrightEmail} /> with the Spot link and what the
            problem is. We will review it and, where appropriate, correct or
            remove it. For copyright specifically, section 9 sets out the formal
            process and is the fastest route.
          </P>
        </>
      ),
    },
    {
      id: 'copyright',
      heading: '9. Copyright and trademark complaints',
      body: (
        <>
          <P>
            We respond to notices of claimed copyright infringement, and we
            terminate the accounts of members who infringe repeatedly.
          </P>
          <H3>Designated agent</H3>
          <P>
            Send copyright notices to our designated agent:
          </P>
          <P>
            Copyright Agent
            <br />
            <Tok value={entity.legalName} />
            <br />
            <Tok value={entity.address} />
            <br />
            <Tok value={entity.copyrightEmail} />
          </P>
          <H3>What a notice must contain</H3>
          <P>
            To be effective under 17 U.S.C. §512(c)(3), your notice must include
            all of the following:
          </P>
          <Ul
            items={[
              'your physical or electronic signature;',
              'identification of the copyrighted work you say has been infringed;',
              'identification of the material you say is infringing, with enough detail for us to find it — a link to the Spot, triby or profile is best;',
              'your name, address, telephone number and email address;',
              'a statement that you have a good-faith belief that the use is not authorised by the copyright owner, its agent or the law;',
              'a statement that the information in your notice is accurate, and that under penalty of perjury you are the owner or authorised to act on the owner’s behalf.',
            ]}
          />
          <P>
            An incomplete notice may delay our response. Note that{' '}
            <strong>
              knowingly misrepresenting that material is infringing can make you
              liable for damages
            </strong>{' '}
            under 17 U.S.C. §512(f), including our legal costs.
          </P>
          <H3>What we do</H3>
          <P>
            If a notice is valid we expeditiously remove or disable access to the
            material, and we tell the member who posted it, passing on your
            notice. We may also remove material we believe infringes without
            waiting for a notice.
          </P>
          <H3>Counter-notice</H3>
          <P>
            If your material was removed and you believe that was a mistake or a
            misidentification, you may send a counter-notice to the address
            above, containing your signature, identification of the removed
            material and where it appeared, a statement under penalty of perjury
            that you have a good-faith belief it was removed by mistake or
            misidentification, and your name, address and telephone number, with
            consent to the jurisdiction of the federal court for your district.
            If we receive a valid counter-notice we forward it to the person who
            complained, and we may restore the material in 10 to 14 business days
            unless they tell us they have filed a court action.
          </P>
          <H3>Repeat infringers</H3>
          <P>
            We keep a record of infringement notices against an account. An
            account that is the subject of repeated valid notices will be
            suspended and then permanently terminated. We apply the same approach
            to members who repeatedly upload photos they have no right to,
            whether or not a formal notice arrives.
          </P>
          <H3>Trademark</H3>
          <P>
            Send trademark complaints to the same address, telling us the mark,
            your registration if you have one, and where the problem appears.
            Remember that using a business&apos;s name to identify its location
            is generally permitted, so please tell us specifically what is
            misleading — a logo used as a Spot photo, or a Spot presented as
            official, are the usual real problems.
          </P>
          <H3>Outside the United States</H3>
          <Ul
            items={[
              <><strong>Canada.</strong> Canada operates a notice-and-notice system under the Copyright Act. If you send us a compliant notice about a Canadian member, our obligation is to forward it to them and keep a record; it does not require us to remove the material. We may still remove it under these Terms.</>,
              <><strong>Mexico.</strong> Send notices under the Ley Federal del Derecho de Autor to the address above, with the same information listed above, and we will act on them.</>,
            ]}
          />
        </>
      ),
    },
    {
      id: 'our-content',
      heading: '10. Our app and our brand',
      body: (
        <>
          <P>
            Tribys — the app, the website, the software, the design, the text we
            wrote, the Tribys name and the Tribys logo — belongs to us and to our
            licensors, and is protected by copyright, trademark and other laws.
          </P>
          <P>
            We give you a personal, limited, revocable, non-exclusive,
            non-transferable licence to install and use the Tribys app on devices
            you control, and to use our website, for your own personal,
            non-commercial use, as long as you comply with these Terms. That is
            the whole of what we grant. Everything not expressly granted is
            reserved.
          </P>
          <P>
            Do not use our name, logo or branding in a way that suggests we
            endorse you, and do not remove or obscure any proprietary notice.
          </P>
        </>
      ),
    },
    {
      id: 'paid',
      heading: '11. Paid features',
      body: (
        <>
          <P>
            Some Tribys features cost money. Tribys Pro is sold as an
            auto-renewing subscription of 1, 3, 6 or 12 months, or as a one-week
            pass that does not renew. A Pro Triby is a one-time purchase that
            lets a free account do a single Pro action. Prices are shown in the
            app before you buy.
          </P>
          <H3>Who you are actually paying</H3>
          <P>
            <strong>
              Purchases are processed by Apple or Google, not by us.
            </strong>{' '}
            They are the merchant of record. We never see or store your card
            details. Their terms govern the transaction itself, and your
            purchase history and payment method live in your App Store or Google
            Play account.
          </P>
          <H3>Subscriptions renew automatically</H3>
          <P>
            A subscription renews for the same period at the then-current price
            until you cancel it. <strong>Cancel through the App Store or Google
            Play</strong> — deleting the Tribys app or your Tribys account does
            not cancel a subscription, and we cannot cancel it for you. Cancel at
            least 24 hours before the period ends, and you keep your benefits
            until it does.
          </P>
          <H3>Refunds</H3>
          <P>
            Refunds are handled by Apple or Google under their policies, so
            please ask them. We can help you work out what happened, but we
            cannot issue a refund for a store purchase. Where the law where you
            live gives you a right to cancel or a right to a refund, that right
            applies and nothing here limits it — in Mexico this includes your
            rights under the Ley Federal de Protección al Consumidor.
          </P>
          <H3>Passes, Pro Tribys and rewards</H3>
          <P>
            The one-week pass gives you Pro for seven days from purchase and then
            ends on its own; nothing renews. A Pro Triby is a credit that is used
            up when the Pro action completes — joining or creating a Pro triby,
            joining as a hidden member, or going past your monthly limit. The
            credit returns to your account if we remove that triby, or if you
            created it and nobody else had joined by its scheduled time, unless
            Apple or Google already refunded the purchase. Referral rewards
            depend on you and the person you referred meeting the conditions
            shown when you share the code; we may change or end a referral
            programme, and we may withhold a reward we believe was obtained
            through fake accounts or other abuse.
          </P>
          <H3>Changes and unused benefits</H3>
          <P>
            We may change prices, and we may add, change or withdraw paid
            features. For a subscription, a price change takes effect at your
            next renewal and we will tell you first so you can cancel. Paid
            benefits have no cash value, are not property, and are not
            transferable. If we terminate your account for breaking these Terms,
            you are not entitled to a refund of unused benefits.
          </P>
        </>
      ),
    },
    {
      id: 'third-party',
      heading: '12. Third-party services',
      body: (
        <>
          <P>
            Tribys connects to services we do not control, and using them means
            accepting their terms as well as ours:
          </P>
          <Ul
            items={[
              'Signing in with Google or Apple.',
              'Linking Spotify to show your top artists on your profile.',
              'Maps and directions, which may open a map app of your choice.',
              'Adding a triby to your device calendar, which may sync to whatever service your calendar uses.',
              'Sharing a link, which sends it wherever you choose to send it.',
            ]}
          />
          <P>
            We are not responsible for those services, their content or their
            availability. If you disconnect one, or it stops working, parts of
            Tribys may stop working too.
          </P>
          <P>
            Links to other websites are not endorsements, and we have no control
            over what is on them.
          </P>
        </>
      ),
    },
    {
      id: 'moderation',
      heading: '13. Moderation, suspension and termination',
      body: (
        <>
          <P>
            We use a mix of automated checks and human review to enforce these
            Terms and the Community Guidelines. The{' '}
            <a href={legalPaths.guidelines}>Community Guidelines</a> describe what
            is automated, what is not, the steps we take, and how to appeal a
            decision — including how to reach a human.
          </P>
          <P>
            <strong>We may remove content, and we may suspend or terminate an
            account.</strong> We do that when someone breaks these Terms or the
            Guidelines, when we are required to by law, when an account appears
            to be compromised or fraudulent, or when we reasonably believe it is
            necessary to protect other members. Where it is reasonable to do so
            we will tell you why, and a serious or unlawful case may mean acting
            immediately and without notice.
          </P>
          <P>
            We may also stop offering Tribys, or any part of it, in your country
            or altogether. If we do, we will give reasonable notice where we can.
          </P>
          <P>
            You can delete your account at any time in Settings. There is a
            30-day window in which signing in restores it; after that it is
            anonymised as described in the{' '}
            <a href={legalPaths.privacy}>Privacy Policy</a>. Deleting your account
            does not cancel a store subscription — see section 11.
          </P>
          <P>
            Sections that should survive the end of this agreement do: 7 (the
            licence you granted, to the extent stated), 9, 10, 14, 15, 16, 17, 18
            and 21.
          </P>
        </>
      ),
    },
    {
      id: 'disclaimers',
      heading: '14. Tribys is provided as it is',
      body: (
        <>
          <P>
            We work hard on Tribys, but we cannot promise it will be perfect.
            To the fullest extent permitted by law, Tribys is provided
            &quot;as is&quot; and &quot;as available&quot;, and we disclaim all
            warranties, express or implied, including implied warranties of
            merchantability, fitness for a particular purpose,
            non-infringement, and any warranty arising from course of dealing or
            usage of trade.
          </P>
          <P>In particular, we do not promise that:</P>
          <Ul
            items={[
              'Tribys will be uninterrupted, secure, timely or error-free;',
              'members are who they say they are, or that our verification will always be right;',
              'other members will behave lawfully, honestly or decently, online or in person;',
              'a Spot exists, is open, is accurate, or is safe;',
              'a triby will have members, or that an event will happen;',
              'automated moderation will catch everything harmful, or that it will never remove something it should not have.',
            ]}
          />
          <P>
            <strong>
              Nothing in these Terms excludes or limits anything that cannot
              lawfully be excluded or limited.
            </strong>{' '}
            Some jurisdictions do not allow the exclusion of certain warranties,
            so parts of this section may not apply to you. If you are a consumer,
            your statutory consumer rights are unaffected — including your rights
            under Mexico&apos;s Ley Federal de Protección al Consumidor and, in
            Canada, applicable provincial consumer protection legislation.
          </P>
        </>
      ),
    },
    {
      id: 'liability',
      heading: '15. Limits on our liability',
      body: (
        <>
          <P>
            To the fullest extent permitted by law, neither Tribys nor its
            members, managers, employees, contractors or suppliers will be liable
            for indirect, incidental, special, consequential, exemplary or
            punitive damages, or for lost profits, lost data, loss of goodwill,
            or business interruption, arising out of or relating to your use of
            Tribys — whether the claim is in contract, tort, negligence, strict
            liability, statute or anything else, and even if we were told such
            damages were possible.
          </P>
          <P>
            To the fullest extent permitted by law, our total liability for all
            claims relating to Tribys is limited to the greater of{' '}
            <strong>
              the amount you paid us in the 12 months before the event giving
              rise to the claim, or one hundred US dollars (US$100)
            </strong>.
          </P>
          <P>
            This includes, specifically, any claim arising from the conduct of
            another member, from a meeting arranged through Tribys, or from
            anything that happens at a Spot or an event. Section 4 explains why:
            we are not there, and we are not part of the arrangement.
          </P>
          <P>
            These limits are a fundamental part of the deal between us — Tribys
            would not be offered on these terms without them. They do not apply
            to liability that cannot lawfully be limited, including, where
            applicable, death or personal injury caused by our negligence, our
            own fraud or fraudulent misrepresentation, or our gross negligence or
            wilful misconduct. If you are a consumer in Mexico or Canada, your
            statutory rights are unaffected.
          </P>
        </>
      ),
    },
    {
      id: 'indemnity',
      heading: '16. Your responsibility for claims about your content and conduct',
      body: (
        <>
          <P>
            You agree to indemnify and hold harmless Tribys and its members,
            managers, employees and contractors against any claim, demand, loss,
            liability, damage, cost or expense — including reasonable legal fees
            — arising out of or relating to:
          </P>
          <Ul
            items={[
              <><strong>your content</strong>, including any claim that it infringes copyright, trademark, publicity or privacy rights — for example a photograph, logo, poster or description you added to a Spot;</>,
              'your use of Tribys, and anything you do through it;',
              'your breach of these Terms, the Community Guidelines, or any law;',
              'any dispute between you and another member, including anything that happens at a meeting, event or Spot;',
              'any claim by a business, venue, organiser or rightsholder about a Spot you created.',
            ]}
          />
          <P>
            We may take over the defence of any such claim at your expense, and
            if we do you agree to cooperate with us. You may not settle a claim in
            a way that imposes any obligation on us without our written consent.
          </P>
          <P>
            If you are a consumer, this section applies only to the extent the
            law where you live permits it.
          </P>
        </>
      ),
    },
    {
      id: 'disputes',
      heading: '17. Resolving disputes',
      body: (
        <>
          <Callout>
            <strong>Please read this section carefully.</strong> It changes how
            legal claims between us are resolved. It requires you to try to
            resolve a dispute with us informally first, then to bring most claims
            in individual arbitration rather than in court, and it means you give
            up the right to a jury and the right to take part in a class action.
            You have 30 days to opt out. It does not apply at all if you live in
            Quebec or Mexico.
          </Callout>
          <H3>17.1 First, talk to us — this step is required</H3>
          <P>
            Most problems are fixable, and we would rather fix them than argue.
            Before either of us starts an arbitration or a lawsuit, the one with
            the complaint must send the other a written{' '}
            <strong>Notice of Dispute</strong>.
          </P>
          <P>
            Send yours to <Tok value={entity.legalEmail} /> with
            &quot;Notice of Dispute&quot; in the subject line, or by post to the
            address in section 22. It must be individual to you, signed by you (or
            by you and your lawyer if you have one), and must include your name,
            the email address on your Tribys account, a description of the
            problem, and what you want us to do about it. We will send ours to
            the email address on your account.
          </P>
          <P>
            <strong>
              We then have 60 days to try to resolve it with you, and completing
              this step is a condition of starting arbitration or going to court.
            </strong>{' '}
            Either of us may ask for an individual telephone or video conference
            during that period, and both of us will take part in good faith. Any
            limitation period, and any deadline for paying a filing fee, is paused
            while this is going on. A demand for arbitration filed before this
            step is finished is invalid, and neither of us may recover the fees of
            filing one. Either of us may ask a court to enforce this paragraph.
          </P>
          <H3>17.2 Then, individual arbitration</H3>
          <P>
            If we have not resolved it within 60 days, either of us may begin
            binding arbitration, and arbitration will be the exclusive way of
            resolving the dispute, apart from the exceptions in 17.3. This
            applies to any dispute or claim relating to Tribys, these Terms, your
            account, or your relationship with us, whenever it arose, including
            claims about advertising and claims that arise after your account
            ends.
          </P>
          <P>
            The arbitration will be administered by the{' '}
            <strong>American Arbitration Association</strong> under its Consumer
            Arbitration Rules, and, where they apply, its Mass Arbitration
            Supplementary Rules, as modified by this section. The rules and forms
            are at <Ext href="https://www.adr.org">adr.org</Ext>. A single
            arbitrator will decide, and will apply the law and honour any claim
            of privilege the law recognises. The arbitrator may award any relief
            a court could award to you individually, including any statutory
            remedy.
          </P>
          <P>
            The arbitration will happen in the county where you live, or remotely
            by telephone, video or documents if you prefer. AAA&apos;s rules
            decide who pays what, and under those rules we pay most of the
            arbitration fees in consumer cases. Nothing in this section requires
            either of us to keep the existence, the substance or the outcome of an
            arbitration confidential.
          </P>
          <P>
            An arbitrator&apos;s decision binds only the parties to that
            arbitration. It is not precedent, and it may not be used to decide
            any other person&apos;s claim.
          </P>
          <H3>17.3 What is not covered</H3>
          <Ul
            items={[
              <><strong>Small claims.</strong> Either of us may bring an individual claim in a small-claims court instead, if it qualifies there.</>,
              <><strong>Intellectual property and misuse.</strong> Either of us may ask a court for an injunction or other equitable relief over intellectual property, or over unauthorised access to, scraping of, or interference with Tribys.</>,
              'Anything a law says cannot be sent to arbitration. In particular, this section does not waive any right to bring a claim to a government agency, or to take part in an agency proceeding.',
            ]}
          />
          <H3>17.4 No class actions</H3>
          <P>
            <strong>
              Arbitration and any court proceeding permitted by 17.3 will be
              individual only. Neither of us may bring a claim as a plaintiff or
              a class member in any class, collective, consolidated,
              representative or private attorney general proceeding, and the
              arbitrator may not consolidate claims or preside over any form of
              class or representative proceeding.
            </strong>{' '}
            You and we each waive any right to a jury trial.
          </P>
          <P>
            <strong>
              This paragraph is not severable from the rest of this section.
            </strong>{' '}
            If it is found unenforceable in whole or in part as to any claim, or
            if a court decides that a claim may proceed on a class,
            collective, representative or private attorney general basis, then
            arbitration does not apply to that claim, and that claim — and only
            that claim — will be decided by a court under section 18. Class-wide
            arbitration is not available under these Terms in any circumstances.
            The rest of this section survives.
          </P>
          <H3>17.5 You can opt out</H3>
          <P>
            You may reject arbitration entirely. Email{' '}
            <Tok value={entity.legalEmail} /> with &quot;Arbitration Opt-Out&quot;
            in the subject line, and include your name and the email address on
            your Tribys account, within{' '}
            <strong>30 days of first accepting these Terms</strong> — or, if we
            later change this section in a way that affects you, within 30 days of
            that change. If you opt out, sections 17.2 and 17.4 do not apply to
            you and disputes go to court under section 18. Opting out has no
            other effect: we will not close your account or treat you
            differently for it.
          </P>
          <H3>17.6 If you live in Quebec or Mexico</H3>
          <Ul
            items={[
              <><strong>Quebec.</strong> Section 11.1 of the Consumer Protection Act prohibits requiring a consumer to arbitrate and prohibits depriving a consumer of a class action. If you are a consumer resident in Quebec, sections 17.2 and 17.4 do not apply to you at all, and you keep your full right to go to court and to be a member of a class action. Only 17.1 applies, and only as a request that you contact us first.</>,
              <><strong>Mexico.</strong> Your rights under the Ley Federal de Protección al Consumidor cannot be waived by contract, and PROFECO&apos;s conciliation and arbitration procedures remain available to you whatever this section says. If you are a consumer resident in Mexico, sections 17.2 and 17.4 do not apply to you.</>,
              <>Elsewhere, if the law where you live makes any part of this section unenforceable against a consumer, that part does not apply to you and the rest still does.</>,
            ]}
          />
          <H3>17.7 Changes</H3>
          <P>
            If we change this section, the change does not apply to a dispute for
            which a Notice of Dispute was already sent, or for which an
            arbitration or court proceeding was already begun.
          </P>
        </>
      ),
    },
    {
      id: 'law',
      heading: '18. Governing law and where claims go',
      body: (
        <>
          <P>
            These Terms are governed by the laws of the State of{' '}
            <Tok value={entity.formedIn} />, and by US federal law where it
            applies, without regard to conflict-of-laws rules. The Federal
            Arbitration Act governs section 17.
          </P>
          <P>
            For any claim that is not resolved in arbitration — because section
            17 does not cover it, because you opted out, or because it is
            excluded — the state and federal courts located in{' '}
            <Tok value={entity.formedIn} /> have exclusive jurisdiction, and you
            and we consent to venue there.
          </P>
          <P>
            <strong>Consumers keep their local protections.</strong> If you are a
            consumer, this section does not deprive you of the protection of the
            mandatory law of the country or province where you live, or of the
            right to bring proceedings there where that law gives it to you.
            Consumers resident in Mexico may bring proceedings before PROFECO or
            the competent Mexican courts, and consumers resident in Quebec may
            bring proceedings in Quebec.
          </P>
        </>
      ),
    },
    {
      id: 'changes',
      heading: '19. Changes to these Terms',
      body: (
        <>
          <P>
            We may change these Terms — for example when we add a feature, or
            when the law changes. The date at the top of this page always shows
            the current version.
          </P>
          <P>
            If a change materially affects your rights we will tell you before it
            takes effect, by a notice in the app, by email, or both, and we will
            give you a reasonable period to read it. Continuing to use Tribys
            after a change takes effect means you accept the new Terms. If you do
            not accept them, stop using Tribys and delete your account.
          </P>
          <P>
            Changes to section 17 are subject to 17.5 and 17.7: you get a fresh
            30-day opt-out, and a change never applies retroactively to a dispute
            already under way. Community Guidelines may be updated separately, and
            section 6 keeps them part of this agreement as updated.
          </P>
        </>
      ),
    },
    {
      id: 'apple',
      heading: '20. If you got Tribys from the Apple App Store',
      body: (
        <>
          <P>
            These terms are required by Apple and apply in addition to
            everything above:
          </P>
          <Ul
            items={[
              'These Terms are between you and us only, not with Apple, and Apple is not responsible for Tribys or its content.',
              'Your licence to use Tribys is limited to non-transferable use on an Apple-branded device you own or control, as permitted by the App Store Terms of Service.',
              'Apple has no obligation to provide any maintenance or support for Tribys.',
              'If Tribys fails to conform to any applicable warranty, you may tell Apple and Apple will refund the purchase price, if any. To the maximum extent permitted by law, Apple has no other warranty obligation with respect to Tribys.',
              'Apple is not responsible for addressing any claim by you or a third party relating to Tribys, including product liability claims, claims that Tribys fails to conform to a legal or regulatory requirement, and claims under consumer protection or privacy law.',
              'If a third party claims that Tribys or your use of it infringes their intellectual property, Apple is not responsible for the investigation, defence, settlement or discharge of that claim.',
              'You confirm that you are not located in a country subject to a US Government embargo or designated as a terrorist-supporting country, and that you are not on any US Government list of prohibited or restricted parties.',
              'Apple and its subsidiaries are third-party beneficiaries of this section, and may enforce it against you.',
            ]}
          />
        </>
      ),
    },
    {
      id: 'general',
      heading: '21. The rest',
      body: (
        <>
          <Ul
            items={[
              <><strong>Whole agreement.</strong> These Terms, together with the Community Guidelines, the Privacy Policy and the Biometric &amp; Consumer Health Data Notice, are the entire agreement between us about Tribys, and replace anything said before.</>,
              <><strong>Severability.</strong> If any provision is unenforceable, it is limited or removed to the minimum extent necessary and the rest stays in force — except section 17.4, which says what happens if it falls.</>,
              <><strong>No waiver.</strong> If we do not enforce something straight away, we have not given up the right to enforce it later.</>,
              <><strong>Assignment.</strong> You may not transfer these Terms or your account. We may transfer them to an affiliate, or in connection with a merger, acquisition or sale of assets, and your rights are unaffected.</>,
              <><strong>Notices.</strong> We may contact you in the app, by push notification, or at the email address on your account. Formal notices to us go to <Tok value={entity.legalEmail} /> and to the postal address below; notices about a legal dispute must also follow 17.1.</>,
              <><strong>No agency.</strong> These Terms do not create a partnership, employment, agency or joint-venture relationship between us.</>,
              <><strong>Force majeure.</strong> Neither of us is liable for a failure to perform caused by something outside our reasonable control.</>,
              <><strong>Language.</strong> This document is also published in Spanish. For consumers resident in Mexico the Spanish version governs; elsewhere, if the two differ, the English version governs.</>,
            ]}
          />
        </>
      ),
    },
    {
      id: 'contact',
      heading: '22. Contact',
      body: (
        <>
          <P>
            <Tok value={entity.legalName} />
            <br />
            <Tok value={entity.address} />
          </P>
          <Ul
            items={[
              <>General support, safety reports and appeals: <Tok value={entity.supportEmail} /></>,
              <>Legal notices, Notices of Dispute and arbitration opt-outs: <Tok value={entity.legalEmail} /></>,
              <>Privacy, and requests about your data: <Tok value={entity.privacyEmail} /></>,
              <>Copyright and trademark notices: <Tok value={entity.copyrightEmail} /></>,
            ]}
          />
        </>
      ),
    },
  ],
};
